/**
 * HiddenEntityCard - v2 content for actions targeting hidden CRM entities.
 *
 * Hidden entities exist in the CRM but are not visible to users (org-user
 * projections, tombstoned external persons, inbound stubs). Background
 * agents read the CRM with include_hidden=True, so they can still propose a
 * timeline event on one; this card is the degraded-render safety net for
 * that case — the viewer's user-scoped store can't hydrate the entity, so a
 * normal card would fall apart. NOTE: approving executes the underlying
 * action only; nothing writes ahoy_hidden=false anywhere, so nothing is
 * "added to the CRM". The original producer of these actions (Gmail/
 * Calendar auto-synced contacts awaiting acknowledgment) was removed with
 * inbound contact removal — see docs/claude/INBOUND_CONTACT_REMOVAL.md.
 *
 * Properties come from the CRM store (the entity already exists) rather than
 * the action payload (which might be a property update or stage change).
 *
 * ## Editing
 *
 * In view mode the DiffSection is intentionally hidden — the entity preview
 * already conveys the intent, and a separate "current → proposed" row
 * duplicates information that's already in the proposal title and the
 * approve label.
 *
 * When the underlying payload is a stage association and the user enters
 * edit mode, the card surfaces a stage-picker DiffSection below the entity
 * preview. Editing the target stage mutates the proposal action payload.
 * Other underlying payload types (property updates, object creates) render
 * without editing.
 *
 * Shell (title, checkbox, expand/collapse) is provided by the parent
 * (ActionAccordion or ActionVersionTimeline).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { ActionStageBadge } from '@/components/proposals/ActionStageBadge';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { AssociatedPipelineStageInput } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { useCrmObjectDiff } from '@/lib/crm/hooks';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';

import type { AssociationAddedEntityPayload } from '../../lib/proposals/core/types';
import {
  isAssociationAddedPayload,
  isTimelineEventPayload,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { useActionEdit } from './ActionAccordion';
import { ActionEntityPreview } from './ActionEntityPreview';
import { type DiffRow, DiffSection } from './DiffSection';
import {
  ActionCardContent,
  useActionPayloadSave,
  useStageEditState,
} from './shared';

/**
 * Stable empty fallback — avoids allocating a new array on every render when
 * the payload isn't a stage association (keeps `useEffect` dependencies
 * referentially stable).
 */
const NO_STAGE_EDIT: null = null;

// ─── Component ───────────────────────────────────────────────────────────────

export function HiddenEntityCard({
  action,
  enrichedData,
  orgId,
  proposalId,
  onUpdate,
}: ActionRendererProps) {
  const entityType = enrichedData?.entity_type_name ?? '';
  const label = enrichedData?.object_label ?? 'Unknown object';

  // Get entity ID from the timeline event payload
  const entityId = isTimelineEventPayload(action.payload)
    ? action.payload.entity_id
    : undefined;

  const contextMessage = isTimelineEventPayload(action.payload)
    ? action.payload.context_message
    : undefined;

  // Fetch entity data from CRM store — hidden entities are fetchable by ID.
  // Still needed locally for `currentStageEntityId` lookup below (the
  // DiffSection surfaces the current stage as part of the stage-association
  // diff). ActionEntityPreview runs its own identical subscription for the
  // entity preview; both reads share the same CRM store so the duplicate
  // hook call is cheap.
  const { projected: storeObject } = useCrmObjectDiff(entityId, orgId);

  // ── Stage editing state ─────────────────────────────────────────────────
  //
  // Hidden-entity adds whose underlying event is `ahoy_association_added`
  // and whose association_type_name is a stage type support inline editing
  // of the target stage. See `isEditableAction` in
  // `lib/proposals/registry/utils.ts` for the gate.

  const { isEditing, setDirty, saveHandlerRef, cancelHandlerRef } =
    useActionEdit();

  const isStageAssocPayload = isAssociationAddedPayload(action.payload);
  const stageAssocPayload = isStageAssocPayload
    ? (action.payload as AssociationAddedEntityPayload)
    : null;
  const associationTypeName =
    stageAssocPayload?.payload.association_type_name ?? '';
  const proposedStageEntityId = stageAssocPayload?.payload.target_entity_id;

  const { associationTypes } = useAssociationTypes(orgId);
  const assocLabel = useMemo(() => {
    if (!associationTypeName) {
      return '';
    }
    const assoc = associationTypes.find(
      a => a.association_type_name === associationTypeName
    );
    return assoc?.label ?? associationTypeName;
  }, [associationTypes, associationTypeName]);

  // Current stage value from the CRM store — hidden entities typically don't
  // have an existing stage association yet, but if they do we surface it so
  // the diff is meaningful ("Current: Lead → Proposed: Opportunity").
  const currentStageEntityId = useMemo(() => {
    if (!associationTypeName || !storeObject?.associations) {
      return undefined;
    }
    const stageAssoc = storeObject.associations.find(
      a => a.association_type_name === associationTypeName
    );
    return stageAssoc?.target_entity_id;
  }, [storeObject, associationTypeName]);

  const { editStageId, handleStageChange, resetToProposed } = useStageEditState(
    {
      proposedValue: proposedStageEntityId ?? NO_STAGE_EDIT,
      orgId,
      entityType,
    }
  );

  const isDirty = useMemo(
    () =>
      isStageAssocPayload &&
      editStageId !== (proposedStageEntityId ?? NO_STAGE_EDIT),
    [isStageAssocPayload, editStageId, proposedStageEntityId]
  );

  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });

  const save = useCallback(async () => {
    if (!stageAssocPayload) {
      throw new Error('Missing context required to save stage edit');
    }
    const updatedPayload = {
      ...stageAssocPayload,
      payload: {
        ...stageAssocPayload.payload,
        target_entity_id: editStageId ?? '',
      },
    };
    await savePayload(updatedPayload);
  }, [stageAssocPayload, editStageId, savePayload]);

  const cancel = useCallback(() => {
    resetToProposed();
  }, [resetToProposed]);

  // Install save/cancel handlers so the shell can invoke them. Re-runs on
  // every render (the refs are mutable) — same pattern as StageAssociationCard.
  useLayoutEffect(() => {
    if (!isStageAssocPayload) {
      return;
    }
    saveHandlerRef.current = save;
    cancelHandlerRef.current = cancel;
  });

  // ── Build diff rows ─────────────────────────────────────────────────────

  const diffRows = useMemo<DiffRow[]>(() => {
    if (!isStageAssocPayload) {
      return [];
    }
    const currentDisplay = currentStageEntityId ? (
      <ActionStageBadge
        value={currentStageEntityId}
        orgId={orgId}
        entityType={entityType}
        size='sm'
        muted
      />
    ) : (
      <span className='text-sm text-[var(--color-text-tertiary)]'>
        (not set)
      </span>
    );
    const proposedDisplay = proposedStageEntityId ? (
      <ActionStageBadge
        value={proposedStageEntityId}
        orgId={orgId}
        entityType={entityType}
        size='sm'
      />
    ) : (
      <span className='text-sm text-[var(--color-text-tertiary)]'>
        (not set)
      </span>
    );
    return [
      {
        id: associationTypeName,
        label: assocLabel,
        currentValue: currentDisplay,
        isEditable: isEditing,
        proposedValue: isEditing ? (
          <AssociatedPipelineStageInput
            value={editStageId}
            onChange={handleStageChange}
            entityType={entityType}
            associationTypeName={associationTypeName}
            ariaLabel={`New value for ${assocLabel}`}
          />
        ) : (
          proposedDisplay
        ),
      },
    ];
  }, [
    isStageAssocPayload,
    currentStageEntityId,
    proposedStageEntityId,
    orgId,
    associationTypeName,
    assocLabel,
    isEditing,
    editStageId,
    handleStageChange,
    entityType,
  ]);

  return (
    <ActionCardContent>
      <ActionEntityPreview
        entityId={entityId}
        entityType={entityType}
        label={label}
        enrichedData={enrichedData}
        orgId={orgId}
      />
      {contextMessage && (
        <MarkdownContent
          className='text-sm text-fg-secondary'
          content={contextMessage}
          orgId={orgId}
          size='compact'
          resolvedSlots={action.enriched_data?.resolved_slots}
        />
      )}
      {isEditing && diffRows.length > 0 && <DiffSection rows={diffRows} />}
    </ActionCardContent>
  );
}
